import { parseLocalDate } from '../services/dates.js';

export const Expenses = {
  name: 'ExpenseManager',
  inject: ['user', 'isAuthenticated'],
  data() {
    return {
      expenses: [],
      loading: true,
      error: null,
      filterCategory: 'all',
      searchTerm: '',
      sortBy: 'date',
      sortOrder: 'desc',
      selectedExpense: null,
      showExpenseModal: false,
      showReceiptOCR: false,
      dateRange: {
        start: '',
        end: ''
      },
      categories: [
        'Office Supplies',
        'Travel',
        'Meals & Entertainment',
        'Software',
        'Equipment',
        'Marketing',
        'Utilities',
        'Insurance',
        'Professional Services',
        'Other'
      ]
    };
  },
  computed: {
    filteredExpenses() {
      let filtered = [...this.expenses];

      // Filter by category
      if (this.filterCategory !== 'all') {
        filtered = filtered.filter(expense => expense.category === this.filterCategory);
      }

      // Filter by search term
      if (this.searchTerm) {
        const term = this.searchTerm.toLowerCase();
        filtered = filtered.filter(expense => 
          expense.merchant?.toLowerCase().includes(term) ||
          expense.description?.toLowerCase().includes(term) ||
          expense.category?.toLowerCase().includes(term)
        );
      }

      // Filter by date range
      if (this.dateRange.start) {
        filtered = filtered.filter(expense => 
          new Date(expense.date) >= new Date(this.dateRange.start)
        );
      }
      if (this.dateRange.end) {
        filtered = filtered.filter(expense => 
          new Date(expense.date) <= new Date(this.dateRange.end)
        );
      }

      // Sort
      filtered.sort((a, b) => {
        let aVal = a[this.sortBy];
        let bVal = b[this.sortBy];

        if (this.sortBy === 'date' || this.sortBy === 'amount') {
          aVal = this.sortBy === 'date' ? new Date(aVal || 0) : parseFloat(aVal || 0);
          bVal = this.sortBy === 'date' ? new Date(bVal || 0) : parseFloat(bVal || 0);
        } else {
          aVal = (aVal || '').toString().toLowerCase();
          bVal = (bVal || '').toString().toLowerCase();
        }

        return this.sortOrder === 'asc' ? (aVal > bVal ? 1 : -1) : (aVal < bVal ? 1 : -1);
      });

      return filtered;
    },
    expenseStats() {
      const stats = {
        totalAmount: 0,
        monthlyAmount: 0,
        totalCount: this.expenses.length,
        categoryBreakdown: {}
      };

      const currentMonth = new Date().getMonth();
      const currentYear = new Date().getFullYear();

      this.expenses.forEach(expense => {
        const amount = parseFloat(expense.amount || 0);
        stats.totalAmount += amount;

        const expenseDate = new Date(expense.date);
        if (expenseDate.getMonth() === currentMonth && expenseDate.getFullYear() === currentYear) {
          stats.monthlyAmount += amount;
        }

        // Category breakdown
        const category = expense.category || 'Other';
        stats.categoryBreakdown[category] = (stats.categoryBreakdown[category] || 0) + amount;
      });

      return stats;
    }
  },
  methods: {
    async fetchExpenses() {
      this.loading = true;
      this.error = null;

      try {
        if (!this.user?.uid) {
          this.loading = false;
          return;
        }

        const expensesRef = firebase.database().ref('expenses');
        const expensesQuery = expensesRef.orderByChild('userId').equalTo(this.user.uid);
        const expensesSnapshot = await expensesQuery.once('value');

        const expenses = [];

        if (expensesSnapshot.exists()) {
          expensesSnapshot.forEach((childSnapshot) => {
            const expense = childSnapshot.val();
            expense.id = childSnapshot.key;
            expenses.push(expense);
          });
        }

        this.expenses = expenses;

      } catch (error) {
        console.error('Error fetching expenses:', error);
        this.error = 'Failed to load expenses. Please try again.';
      } finally {
        this.loading = false;
      }
    },

    async deleteExpense(expense) {
      if (!confirm(`Delete expense "${expense.merchant}"?`)) {
        return;
      }

      try {
        await firebase.database().ref(`expenses/${expense.id}`).remove();
        
        // Remove from local array
        const index = this.expenses.findIndex(exp => exp.id === expense.id);
        if (index !== -1) {
          this.expenses.splice(index, 1);
        }

      } catch (error) {
        console.error('Error deleting expense:', error);
        alert('Error deleting expense. Please try again.');
      }
    },

    formatCurrency(amount) {
      return new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency: 'USD'
      }).format(amount || 0);
    },

    formatDate(dateStr) {
      const date = parseLocalDate(dateStr);
      return date ? date.toLocaleDateString('en-US') : 'N/A';
    },

    setCategoryColor(category) {
      const colors = {
        'Office Supplies': 'alert-primary',
        'Travel': 'alert-success',
        'Meals & Entertainment': 'alert-warning text-dark',
        'Software': 'alert-info',
        'Equipment': 'alert-secondary text-dark',
        'Marketing': 'alert-danger',
        'Utilities': 'alert-dark',
        'Insurance': 'alert-success',
        'Professional Services': 'alert-primary',
        'Other': 'alert-secondary text-dark'
      };
      return colors[category] || 'bg-secondary';
    },

    setSortBy(field) {
      if (this.sortBy === field) {
        this.sortOrder = this.sortOrder === 'asc' ? 'desc' : 'asc';
      } else {
        this.sortBy = field;
        this.sortOrder = 'desc';
      }
    },

    viewExpense(expense) {
      this.selectedExpense = expense;
      this.showExpenseModal = true;
    },

    closeExpenseModal() {
      this.showExpenseModal = false;
      this.selectedExpense = null;
    },

    openReceiptOCR() {
      this.showReceiptOCR = true;
    },

    closeReceiptOCR() {
      this.showReceiptOCR = false;
    },

    onExpenseSaved(expense) {
      this.expenses.unshift(expense);
      this.closeReceiptOCR();
    },

    clearFilters() {
      this.dateRange = { start: '', end: '' };
      this.searchTerm = '';
      this.filterCategory = 'all';
    }
  },

  async mounted() {
    await this.fetchExpenses();
  },

  template: '#expenses'
};
